import { useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { type AddContainers } from '@sr/types';
import {
  FileUpload,
  type FileUploadObj,
  type UploadStatus,
} from 'component-library/components/Input/FileUpload';
import { useFileUpload } from '#lib/hooks/useFileUpload';

export const AddContainersFileUpload = () => {
  const [uploadStatus, setUploadStatus] = useState<Record<string, UploadStatus>>({});
  const [fileState, setFileState] = useState<FileUploadObj[]>([]);
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<AddContainers>();

  const mutateFunc = useFileUpload({
    onMutate: (fileObj: FileUploadObj) => {
      setUploadStatus((prevStatus) => ({ ...prevStatus, [fileObj.id]: 'uploading' }));
    },
    onSuccess: (_, fileObj) => {
      setUploadStatus((prevStatus) => ({ ...prevStatus, [fileObj.id]: 'completed' }));
    },
    onError: (_, fileObj) => {
      setUploadStatus((prevStatus) => ({ ...prevStatus, [fileObj.id]: 'failed' }));

      const filteredFiles = fileState.filter((file) => file.id !== fileObj.id);
      const mappedFiles = filteredFiles.map((fileItem) => ({
        id: fileItem.id,
        fileName: fileItem.file.name,
      }));

      // If upload fails, remove from form state
      setValue('orderDetails.fileAttachments', mappedFiles);
    },
  });

  const files = useWatch({
    control: control,
    name: 'orderDetails.fileAttachments',
    defaultValue: [],
  });

  useEffect(() => {
    if (files?.length && !fileState.length) {
      const newFileState = files.map((file) => ({
        id: file.id,
        fileName: file.fileName,
        file: new File([], file.fileName),
      }));

      setFileState(newFileState);
    }
  }, [fileState, files]);

  const onFileAddedHandler = (fileObj: FileUploadObj) => {
    mutateFunc.mutate(fileObj);
  };

  return (
    <Controller
      name={'orderDetails.fileAttachments'}
      control={control}
      render={({ field: { onChange } }) => {
        return (
          <FileUpload
            uploadStatus={uploadStatus}
            label={t('add.containers.fileupload.drop.label')}
            filesState={fileState}
            onChange={(files) => {
              setFileState(files);

              const mappedFiles = files.map((fileObj) => ({
                id: fileObj.id,
                fileName: fileObj.file.name,
              }));

              onChange(mappedFiles);
            }}
            onFileAdded={onFileAddedHandler}
            description={t('add.containers.fileupload.description')}
          />
        );
      }}
    />
  );
};
